import { useState, useEffect } from 'react';
import { X, Search, Trash2, Calendar, Tag, Edit2 } from 'lucide-react';
import { modelApi } from '../../utils/api';
import useFlowStore from '../../stores/useFlowStore';
import { formatDate } from '../../utils/helpers';
import { useConfirm } from '../../hooks/useConfirm';
import ConfirmDialog from './ConfirmDialog';
import RenameModal from './RenameModal';
import toast from 'react-hot-toast';

const LoadModelModal = ({ onClose }) => {
  const { setCurrentModel, isDirty, modelName, nodes } = useFlowStore();
  const [models, setModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [renameModel, setRenameModel] = useState(null);
  const { isOpen: confirmOpen, config: confirmConfig, confirm, cancel } = useConfirm();

  useEffect(() => {
    fetchModels();
  }, []);

  useEffect(() => {
    filterModels();
  }, [searchTerm, filterType, models]);

  const fetchModels = async () => {
    setIsLoading(true);
    try {
      const response = await modelApi.getAll();
      const rawData = response?.data !== undefined ? response.data : response;
      const list = Array.isArray(rawData)
        ? rawData
        : (Array.isArray(rawData?.data) ? rawData.data : []);
      setModels(list);
    } catch (error) {
      toast.error(`Failed to load models: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const filterModels = () => {
    let filtered = models;

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter((m) => m.type === filterType);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((m) =>
        m.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredModels(filtered);
  };

  const handleLoad = async (modelId) => {
    // Check if there are unsaved changes
    if (isDirty && modelName && nodes.length > 0) {
      await confirm({
        title: 'Unsaved Changes',
        message: `You have unsaved changes in "${modelName}". Loading another model will discard these changes. Do you want to continue?`,
        confirmText: 'Discard & Load',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm: async () => {
          try {
            const response = await modelApi.getById(modelId);
            const modelData = response?.data?.id ? response.data : (response?.id ? response : (response?.data || response));
            setCurrentModel(modelData);
            toast.success('Model loaded successfully!');
            onClose();
          } catch (error) {
            toast.error(`Failed to load model: ${error.message}`);
          }
        }
      });
    } else {
      try {
        const response = await modelApi.getById(modelId);
        const modelData = response?.data?.id ? response.data : (response?.id ? response : (response?.data || response));
        setCurrentModel(modelData);
        toast.success('Model loaded successfully!');
        onClose();
      } catch (error) {
        toast.error(`Failed to load model: ${error.message}`);
      }
    }
  };

  const handleRename = async (newName) => {
    if (!renameModel) return;
    
    try {
      await modelApi.rename(renameModel._id, newName);
      toast.success('Model renamed successfully!');
      fetchModels();
    } catch (error) {
      toast.error(`Failed to rename model: ${error.message}`);
      throw error;
    }
  };

  const handleDelete = async (modelId, modelName) => {
    await confirm({
      title: 'Delete Model',
      message: `Are you sure you want to delete model "${modelName}"? This action cannot be undone and all data will be permanently lost.`,
      confirmText: 'Delete Model',
      cancelText: 'Cancel',
      type: 'danger',
      onConfirm: async () => {
        try {
          await modelApi.delete(modelId);
          toast.success('Model deleted successfully!');
          fetchModels();
        } catch (error) {
          toast.error(`Failed to delete model: ${error.message}`);
        }
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Load Model
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
          <div className="relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search models..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                filterType === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterType('relational')}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                filterType === 'relational'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Relational
            </button>
            <button
              onClick={() => setFilterType('nosql')}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                filterType === 'nosql'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              NoSQL
            </button>
            <button
              onClick={() => setFilterType('analytical')}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                filterType === 'analytical'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Analytical
            </button>
          </div>
        </div>

        {/* Models List */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : filteredModels.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No models found
            </div>
          ) : (
            <div className="space-y-2">
              {filteredModels.map((model) => (
                <div
                  key={model._id}
                  className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-500 transition-colors cursor-pointer group"
                  onClick={() => handleLoad(model._id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {model.name}
                      </h3>
                      {model.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                          {model.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Tag size={12} />
                          {model.type}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {formatDate(model.metadata?.createdAt)}
                        </span>
                      </div>
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenameModel(model);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                        title="Rename model"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(model._id, model.name);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                        title="Delete model"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>

        {/* Rename Modal */}
        <RenameModal
          isOpen={!!renameModel}
          currentName={renameModel?.name || ''}
          itemType="Model"
          onClose={() => setRenameModel(null)}
          onRename={handleRename}
        />

        {/* Confirm Dialog */}
        <ConfirmDialog
          isOpen={confirmOpen}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmText={confirmConfig.confirmText}
          cancelText={confirmConfig.cancelText}
          type={confirmConfig.type}
          onConfirm={confirmConfig.onConfirm}
          onCancel={cancel}
        />
      </div>
    </div>
  );
};

export default LoadModelModal;
